import dayjs from 'dayjs';
import { PropTypes } from 'prop-types';
import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AlertTemplateFieldType, FormMode, DateTimeFormats, AlertType } from '@ge/models/constants';
import {
  DateMetaField,
  DateTimeMetaField,
  NumericInputMetaField,
  SelectMetaField,
  TextMetaField,
} from '@ge/shared/components/meta-fields';
import { StyledDateContainer } from '@ge/shared/components/tasks/task-template-shared';
import { getFriendlyTimestamp } from '@ge/shared/util';

import { AlertViewField } from '../alert-shared';

import { BlockedActionsField } from './blocked-actions-field';

// will this defaultValue (esp if empty) override the default value on an existing alert bound to the form higher up the chain?

const START_TIME_FIELD = 'startTime';
const VALID_TO_FIELD = 'validTo';

export const AlertField = React.memo(function AlertField({
  defaultValue,
  mode,
  name,
  ...metadata
}) {
  const { ready, t } = useTranslation(['alerts'], { useSuspense: false });

  const { control, getValues } = useFormContext();

  let value = useWatch({ control, defaultValue, name });

  const isDateTime = metadata?.type === AlertTemplateFieldType.DATE_TIME;
  const isDerateDate = name === START_TIME_FIELD || name === VALID_TO_FIELD;
  const needsCrossFieldCheck = isDateTime && isDerateDate;
  const isDerateAlert = metadata?.alertType === AlertType.DERATE;

  const peerName = needsCrossFieldCheck
    ? name === START_TIME_FIELD
      ? VALID_TO_FIELD
      : START_TIME_FIELD
    : null;

  const combinedValidators = useMemo(() => {
    const templateValidators = metadata.validators || [];

    if (isDerateAlert && needsCrossFieldCheck && peerName) {
      const crossFieldValidator = (currentValue) => {
        if (!currentValue) return true;
        const peerValue = getValues(peerName);
        if (!peerValue) return true;
        const currentMoment = dayjs(currentValue);
        const peerMoment = dayjs(peerValue);

        if (currentMoment.isSame(peerMoment, 'minute')) {
          return t(
            'validation.startTimeEqualsValidTo',
            `${t(`form.${name}.label`, name)} and ${t(
              `form.${peerName}.label`,
              peerName,
            )} cannot be the same.`,
          );
        }

        if (name === START_TIME_FIELD) {
          if (currentMoment.isAfter(peerMoment, 'minute')) {
            return t(
              'validation.startTimeAfterValidTo',
              `Start time cannot be after ${t(`form.${peerName}.label`, peerName)}.`,
            );
          }
        } else {
          if (currentMoment.isBefore(peerMoment, 'minute')) {
            return t(
              'validation.validToBeforeStartTime',
              `Valid To time cannot be before ${t(`form.${peerName}.label`, peerName)}.`,
            );
          }
        }
        return true;
      };

      return [...templateValidators, { validate: crossFieldValidator }];
    }

    return templateValidators;
  }, [isDerateAlert, needsCrossFieldCheck, name, peerName, metadata.validators, t, getValues]);

  if (!ready) return null;

  if (metadata?.type === AlertTemplateFieldType.BLOCKED_ACTIONS) {
    return <BlockedActionsField values={metadata.values} />;
  }

  const translations = {
    label: t(`form.${name}.label`, name),
    placeholderLabel: t(`form.${name}.placeholder_label`, ''),
  };

  if (mode === FormMode.VIEW) {
    // if we have options then get label to display in view mode
    if (metadata?.values?.length) {
      value = metadata.values.find(({ value: _value }) => value === _value)?.label ?? value;
    }
    if (isDateTime && value) {
      const timeZone = getFriendlyTimestamp(new Date()).replace(/.*(\([^)]+\)).*/, '$1');
      value = `${dayjs(value).format(DateTimeFormats.DEFAULT_DATE_TIME)} ${timeZone}`;
    }
    return <AlertViewField label={translations.label} value={value} />;
  }

  switch (metadata?.type) {
    case AlertTemplateFieldType.DATE:
      return (
        <StyledDateContainer>
          <div className="label">{translations.label}</div>
          <DateMetaField
            defaultSelection={metadata.defaultSelection}
            defaultValue={defaultValue}
            metadata={metadata}
            name={name}
            validators={metadata.validators}
          />
        </StyledDateContainer>
      );

    case AlertTemplateFieldType.DATE_TIME:
      return (
        <DateTimeMetaField
          defaultSelection={metadata.defaultSelection}
          defaultValue={defaultValue}
          label={translations.label}
          metadata={metadata}
          name={name}
          validators={combinedValidators}
          triggerPeer={peerName}
        />
      );
    case AlertTemplateFieldType.SELECT: {
      return (
        <SelectMetaField
          defaultValue={defaultValue ?? metadata.defaultSelection ?? metadata.values?.[0]?.value}
          label={translations.label}
          metadata={metadata}
          name={name}
          options={metadata.values}
          primary={metadata.emphasis === 1}
          searchable={false}
        />
      );
    }
    case AlertTemplateFieldType.NUMBER:
      return (
        <NumericInputMetaField
          defaultValue={defaultValue}
          label={translations.label}
          maxLength={metadata.maxLength}
          metadata={metadata}
          name={name}
          placeholder={translations.placeholderLabel}
          tabIndex={1}
        />
      );
    
    case AlertTemplateFieldType.TEXT:
      return (
        <TextMetaField
          defaultValue={defaultValue}
          label={translations.label}
          maxLength={metadata.maxLength}
          metadata={metadata}
          name={name}
          placeholder={translations.placeholderLabel}
          tabIndex={1}
        />
      );

    default:
      return null;
  }
});

AlertField.propTypes = {
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hidden: PropTypes.bool,
  maxLength: PropTypes.number,
  mode: PropTypes.oneOf(Object.values(FormMode)),
  name: PropTypes.string.isRequired,
};

AlertField.defaultProps = {
  defaultValue: undefined,
  hidden: false,
  maxLength: undefined,
  mode: FormMode.VIEW,
};
